import { useCallback } from 'react'
import { Currency } from '@sequencehq/api'
import { useNotifications } from '@sequencehq/core-components'
import { WaterfallData } from '../types'
import { getCellValue } from '../utils/tableCellUtils'
import type { CellPosition } from './useTableSelection'

type UseTableKeyboardProps = {
  currentPosition: CellPosition
  setCurrentPosition: (position: CellPosition) => void
  selectedRange: { start: CellPosition; end: CellPosition } | null
  selectionStart: CellPosition | null
  setSelectedRange: (
    range: { start: CellPosition; end: CellPosition } | null
  ) => void
  setSelectionStart: (pos: CellPosition | null) => void
  clearSelection: () => void
  allRows: any[]
  data: WaterfallData
  currency: Currency
}

export const useTableKeyboard = ({
  currentPosition,
  setCurrentPosition,
  selectedRange,
  selectionStart,
  setSelectedRange,
  setSelectionStart,
  clearSelection,
  allRows,
  data,
  currency
}: UseTableKeyboardProps) => {
  const { displayNotification } = useNotifications()

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const maxRow = allRows.length - 1
      const maxCol = data.monthlyColumns.length + 3
      const curRow = Math.max(0, currentPosition.row)
      const curCol = Math.max(0, currentPosition.col)

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          if (e.shiftKey) {
            // Extend selection left
            const anchor =
              selectedRange?.start || selectionStart || currentPosition
            const next = {
              row: curRow,
              col: Math.max(0, curCol - 1)
            }
            setCurrentPosition(next)
            setSelectionStart(anchor)
            setSelectedRange({ start: anchor, end: next })
          } else {
            const next = {
              row: curRow,
              col: Math.max(0, (curCol < 0 ? 0 : curCol) - 1)
            }
            setCurrentPosition(next)
            setSelectionStart(next)
            setSelectedRange({ start: next, end: next })
          }
          break
        case 'ArrowRight':
          e.preventDefault()
          if (e.shiftKey) {
            // Extend selection right
            const anchor =
              selectedRange?.start || selectionStart || currentPosition
            const next = {
              row: curRow,
              col: Math.min(maxCol, curCol + 1)
            }
            setCurrentPosition(next)
            setSelectionStart(anchor)
            setSelectedRange({ start: anchor, end: next })
          } else {
            const next = {
              row: curRow,
              col: Math.min(maxCol, (curCol < 0 ? 0 : curCol) + 1)
            }
            setCurrentPosition(next)
            setSelectionStart(next)
            setSelectedRange({ start: next, end: next })
          }
          break
        case 'ArrowUp':
          e.preventDefault()
          if (e.shiftKey) {
            // Extend selection up
            const anchor =
              selectedRange?.start || selectionStart || currentPosition
            const next = {
              row: Math.max(0, curRow - 1),
              col: curCol
            }
            setCurrentPosition(next)
            setSelectionStart(anchor)
            setSelectedRange({ start: anchor, end: next })
          } else {
            const next = {
              row: Math.max(0, (curRow < 0 ? 0 : curRow) - 1),
              col: curCol
            }
            setCurrentPosition(next)
            setSelectionStart(next)
            setSelectedRange({ start: next, end: next })
          }
          break
        case 'ArrowDown':
          e.preventDefault()
          if (e.shiftKey) {
            // Extend selection down
            const anchor =
              selectedRange?.start || selectionStart || currentPosition
            const next = {
              row: Math.min(maxRow, curRow + 1),
              col: curCol
            }
            setCurrentPosition(next)
            setSelectionStart(anchor)
            setSelectedRange({ start: anchor, end: next })
          } else {
            const next = {
              row: Math.min(maxRow, (curRow < 0 ? 0 : curRow) + 1),
              col: curCol
            }
            setCurrentPosition(next)
            setSelectionStart(next)
            setSelectedRange({ start: next, end: next })
          }
          break
        case ' ': {
          e.preventDefault()
          const currentRow = allRows[currentPosition.row]
          if (currentRow && currentRow.getCanExpand()) {
            currentRow.toggleExpanded()
          }
          break
        }
        case 'Escape': {
          e.preventDefault()
          clearSelection()
          break
        }
        case 'c': {
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault()

            if (selectedRange) {
              const minRow = Math.min(
                selectedRange.start.row,
                selectedRange.end.row
              )
              const maxRow = Math.max(
                selectedRange.start.row,
                selectedRange.end.row
              )
              const minCol = Math.min(
                selectedRange.start.col,
                selectedRange.end.col
              )
              const maxCol = Math.max(
                selectedRange.start.col,
                selectedRange.end.col
              )

              const rows = []
              for (let r = minRow; r <= maxRow; r++) {
                const row = allRows[r]
                if (!row) continue

                const cols = []
                for (let c = minCol; c <= maxCol; c++) {
                  const value = getCellValue(
                    row,
                    c,
                    data.monthlyColumns,
                    currency
                  )
                  cols.push(value)
                }
                rows.push(cols.join('\t'))
              }

              const clipboardText = rows.join('\n')
              navigator.clipboard.writeText(clipboardText)
              displayNotification('Selection copied to clipboard', {
                type: 'success',
                duration: 2000
              })
            } else {
              const currentRow = allRows[currentPosition.row]
              if (currentRow) {
                const value = getCellValue(
                  currentRow,
                  currentPosition.col,
                  data.monthlyColumns,
                  currency
                )
                if (value) {
                  navigator.clipboard.writeText(value)
                  displayNotification('Copied to clipboard', {
                    type: 'success',
                    duration: 2000
                  })
                }
              }
            }
          }
          break
        }
      }
    },
    [
      currentPosition,
      allRows,
      data.monthlyColumns,
      currency,
      selectedRange,
      selectionStart,
      setSelectedRange,
      setSelectionStart,
      clearSelection,
      setCurrentPosition,
      displayNotification
    ]
  )

  return { handleKeyDown }
}
